import { InputHTMLAttributes } from 'react';

import { Label, Radio } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useField } from 'formik';
import { camelCase } from 'lodash-es';
import { useFormContext } from 'react-hook-form';

import { InputError } from '../../../../components/InputError/InputError';
import { extractError } from '../../../../utils/ReactHookForm';

export const RadioButton = ({
  name,
  labelText,
  options,
  labelDescriptionText,
  className,
  compact,
  ...props
}: {
  name: string;
  labelText: string;
  labelDescriptionText?: string;
  options: readonly {
    label: string;
    value: string | number | readonly string[] | undefined;
  }[];
  className?: string;
  compact?: boolean;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const [{ onChange, ...field }, { error, touched }] = useField(name);
  return (
    <>
      <fieldset
        className={clsx(className, 'mb-0 border-none p-0', {
          'grid grid-cols-2 gap-x-s': compact,
        })}
      >
        <legend
          className={clsx('inline-block p-0 align-middle text-primary-10', {
            'mb-s': !labelDescriptionText,
          })}
        >
          {labelText}
        </legend>
        {labelDescriptionText && (
          <p className="mb-l mt-0 text-s font-reg text-structure-40">
            {labelDescriptionText}
          </p>
        )}
        {options.map(({ label, value }) => {
          const id = camelCase(`${name}_${label}`);
          const checked = value === field.value;

          return (
            <div
              className={clsx(
                'flex max-w-[400px] items-center rounded-xs border border-structure-80 pl-r',
                compact ? 'm-0' : 'mb-r',
                {
                  'border-primary-40 shadow-blue-m': checked,
                  'hover:bg-secondary-97': !checked,
                },
              )}
              key={id}
            >
              <Radio
                {...field}
                {...props}
                onChange={(e) => {
                  props?.onChange?.(e);
                  onChange(e);
                }}
                checked={checked}
                id={id}
                name={name}
                value={value}
                className="!border !border-structure-60 checked:!border-none"
              />
              <Label className="!m-0 flex-1 p-r !font-med" htmlFor={id}>
                {label}
              </Label>
            </div>
          );
        })}
        <InputError isVisible={touched} error={error} />
      </fieldset>
    </>
  );
};

export const HookFormRadioButton = ({
  name,
  labelText,
  options,
  labelDescriptionText,
  className,
  compact,
  onChange,
}: {
  name: string;
  labelText: string;
  labelDescriptionText?: string;
  options: readonly {
    label: string;
    value: string | number | readonly string[] | undefined;
  }[];
  className?: string;
  compact?: boolean;
} & InputHTMLAttributes<HTMLInputElement>) => {
  const { register, formState, watch } = useFormContext();
  const currentValue = watch(name);
  const error = extractError(formState.errors[name]);
  const showError = formState.isSubmitted;

  return (
    <fieldset
      className={clsx(className, 'mb-0 border-none p-0', {
        'grid grid-cols-2 gap-x-s': compact,
      })}
    >
      <legend
        className={clsx('inline-block p-0 align-middle text-primary-10', {
          'mb-s': !labelDescriptionText,
        })}
      >
        {labelText}
      </legend>
      {labelDescriptionText && (
        <p className="mb-l mt-0 text-s font-reg text-structure-40">
          {labelDescriptionText}
        </p>
      )}
      {options.map(({ label, value }) => {
        const id = camelCase(`${name}_${label}`);
        const isChecked = value === currentValue;

        return (
          <div
            className={clsx(
              'flex max-w-[400px] items-center rounded-xs border border-structure-80 pl-r',
              compact ? 'm-0' : 'mb-r',
              {
                'border-primary-40 shadow-blue-m': isChecked,
                'hover:bg-secondary-97': !isChecked,
              },
            )}
            key={id}
          >
            <Radio
              {...register(name, {
                onChange: (e) => {
                  onChange?.(e);
                },
              })}
              id={id}
              value={value}
              checked={isChecked}
              className="!border !border-structure-60 checked:!border-none"
            />
            <Label className="!m-0 flex-1 p-r !font-med" htmlFor={id}>
              {label}
            </Label>
          </div>
        );
      })}
      <div className={clsx({ 'col-span-2': compact })}>
        <InputError isVisible={showError} error={error} />
      </div>
    </fieldset>
  );
};
